/**
 * Saldo: per person, the vacation entitlement, what is used, what is planned and what is left,
 * the next absence, and egenmelding against its limit (owner, 2026-09-21).
 *
 * Gjenstår is the person's balance field as it stands, vacation_days_balance for this year and
 * next_year_vacation_days_balance for next; they are the only years a balance is stored for, so
 * they are the only years offered. Brukt and Planlagt are summed from the approved Ferie
 * absences: what each was charged (charged_days, stored when approved, split at the new year the
 * way the server splits it), or for a row charged before that column existed, its calendar days,
 * which is what the old code took (utils/absenceRequestUtils.js chargedOf). Planlagt is approved
 * and not started; Brukt is started. The three add up to Rettighet unless the balance was changed
 * some other way (set by hand, carried over by Nytt ferieår, a starting balance, a charge over the
 * balance): that difference is Justert, so Rettighet + Justert − Brukt − Planlagt = Gjenstår on every row.
 */
import type { ApiAbsence, ApiAbsenceType, ApiUser } from '@/api/data';
import { addDays, toUTC } from '@timefly/core';
import { lateStartWeek } from '../ansatt/summary';
import { type Category, catOf } from './category';
import { spanOf } from './dates';

export interface Balances {
  vacation_days_balance?: number | null;
  next_year_vacation_days_balance?: number | null;
  total_vacation_days_per_year?: number | null;
  /** «Ansatt fra» here, YYYY-MM-DD; on GET /users for the people.manage reader, the one who sees Saldo */
  employment_start?: string | null;
}
export interface SaldoRow<U> {
  u: U;
  entitled: number | null;
  /** ferieloven § 5 nr. 3: the week the year holds for a start after 30 September of it, else null */
  late: number | null;
  used: number;
  planned: number;
  left: number | null;
  /** what moved the balance besides approved holiday: left − (entitled − used − planned); null when either is unknown */
  adjusted: number | null;
  next: ApiAbsence | undefined;
  /** egenmelding in the last twelve months, against the type's limits when it has them */
  egen: { times: number; days: number; maxTimes: number | null; maxDays: number | null };
}

const days = (from: string, to: string) =>
  to < from ? 0 : Math.round((toUTC(to).getTime() - toUTC(from).getTime()) / 864e5) + 1;

/** What one approved absence took from year `year`'s balance. */
export function chargeIn(a: ApiAbsence, year: number): number {
  const { from, to } = spanOf(a);
  const y0 = Number(from.slice(0, 4));
  const y1 = Number(to.slice(0, 4));
  if (year < y0 || year > y1) return 0;
  if (a.charged_days != null) {
    const next = Number(a.charged_days_next_year ?? 0);
    if (y0 === y1) return Number(a.charged_days);
    return year === y0 ? Number(a.charged_days) - next : next;
  }
  const s = `${year}-01-01` > from ? `${year}-01-01` : from;
  const e = `${year}-12-31` < to ? `${year}-12-31` : to;
  return days(s, e);
}

export function saldoRows<U extends Pick<ApiUser, 'id'> & Balances>(
  users: U[],
  absences: ApiAbsence[],
  types: Pick<ApiAbsenceType, 'category' | 'is_active' | 'max_times' | 'max_days_per_period'>[],
  o: { year: number; today: string; basis?: string | undefined },
): SaldoRow<U>[] {
  const thisYear = Number(o.today.slice(0, 4));
  const egenType = types.find((t) => t.is_active && catOf(t) === 'egenmelding');
  const since = addDays(o.today, -364);
  return users.map((u) => {
    const own = absences.filter((a) => a.user_id === u.id);
    const ferie = own.filter((a) => a.status === 'accepted' && catOf(a.absence_type) === 'ferie');
    let used = 0;
    let planned = 0;
    for (const a of ferie) {
      const n = chargeIn(a, o.year);
      if (spanOf(a).from > o.today) planned += n;
      else used += n;
    }
    const egen = own.filter(
      (a) => a.status === 'accepted' && catOf(a.absence_type) === 'egenmelding' && spanOf(a).to >= since,
    );
    const next = own
      .filter((a) => a.status !== 'declined' && spanOf(a).to >= o.today)
      .sort((a, b) => a.start_date.localeCompare(b.start_date))[0];
    const left =
      o.year === thisYear
        ? (u.vacation_days_balance ?? null)
        : o.year === thisYear + 1
          ? (u.next_year_vacation_days_balance ?? null)
          : null;
    // the year's entitlement is one week for a start after 30 September of it (ADR 0024), as on the employee page
    const late = lateStartWeek(u.employment_start, `${o.year}-01-01`, o.basis);
    const per = u.total_vacation_days_per_year ?? null;
    const entitled = late != null && per ? Math.min(late, per) : per;
    return {
      u,
      entitled,
      late,
      used,
      planned,
      left,
      adjusted:
        left != null && entitled != null ? Math.round((left - (entitled - used - planned)) * 10) / 10 : null,
      next,
      egen: {
        times: egen.length,
        days: egen.reduce((n, a) => {
          const { from, to } = spanOf(a);
          return n + days(from < since ? since : from, to > o.today ? o.today : to);
        }, 0),
        maxTimes: egenType?.max_times ?? null,
        maxDays: egenType?.max_days_per_period ?? null,
      },
    };
  });
}

/** Whether a person has an approved or pending absence of a category in the year: the Type filter on Saldo. */
export const hasCategoryIn = (absences: ApiAbsence[], uid: number, cat: Category, year: number) =>
  absences.some((a) => {
    if (a.user_id !== uid || a.status === 'declined' || catOf(a.absence_type) !== cat) return false;
    const { from, to } = spanOf(a);
    return from.slice(0, 4) <= String(year) && to.slice(0, 4) >= String(year);
  });

/** The three cards: average left, people with vacation planned, people at five days or fewer. */
export function saldoSummary(rows: SaldoRow<unknown>[]) {
  const known = rows.filter((r) => r.left != null);
  const avg = known.length ? known.reduce((n, r) => n + (r.left ?? 0), 0) / known.length : null;
  const ent = rows.filter((r) => r.entitled != null);
  return {
    avgLeft: avg,
    avgEntitled: ent.length ? ent.reduce((n, r) => n + (r.entitled ?? 0), 0) / ent.length : null,
    withPlanned: rows.filter((r) => r.planned > 0).length,
    low: known.filter((r) => (r.left ?? 0) <= 5).length,
    total: rows.length,
  };
}
